import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Renderer2,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';

import { Select } from 'primeng/select';

import { FormFieldControlAdapter } from './form-field-control-adapter';

@Directive({
  selector: 'p-select[formFieldControl]',
  standalone: true,
  providers: [
    {
      provide: FormFieldControlAdapter,
      useExisting: forwardRef(() => FormFieldPrimengSelectDirective),
    },
  ],
})
export class FormFieldPrimengSelectDirective
  extends FormFieldControlAdapter
  implements AfterViewInit
{
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly select = inject(Select);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly generatedId = this.generateControlId();
  private readonly initialHostId = this.readInitialHostId();

  readonly idInput = input<string | null>(null, { alias: 'id' });
  private readonly fallbackHostId = signal<string | null>(null);
  private readonly labelledByIdsState = signal<string[]>([]);
  private readonly describedByIdsState = signal<string[]>([]);
  private readonly invalidState = signal(false);
  private readonly requiredState = signal(false);

  readonly id = computed(() => this.idInput() ?? this.initialHostId ?? this.fallbackHostId() ?? this.generatedId);
  readonly labelForId = computed(() => null);
  readonly ariaLabelledBy = computed(() => {
    const ids = this.labelledByIdsState();
    return ids.length > 0 ? ids.join(' ') : null;
  });
  private readonly ariaDescribedBy = computed(() => {
    const ids = this.describedByIdsState();
    return ids.length > 0 ? ids.join(' ') : null;
  });

  constructor() {
    super();

    effect(() => {
      const invalid = this.invalidState();
      const required = this.requiredState();
      const describedBy = this.ariaDescribedBy();

      this.select.inputId = this.id();
      this.select.ariaLabelledBy = this.ariaLabelledBy() ?? undefined;
      this.toggleHostClass('p-invalid', invalid);
      this.select.directivePT.set({
        label: {
          'aria-describedby': describedBy ?? undefined,
          'aria-required': required ? 'true' : undefined,
          required: required ? '' : undefined,
        },
      });
      this.changeDetectorRef.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    const hostId = this.hostElement.nativeElement.getAttribute('id')?.trim();
    if (hostId) {
      this.fallbackHostId.set(hostId);
    }
  }

  setLabelledByIds(ids: string[]): void {
    this.labelledByIdsState.set(ids.filter((id) => id.trim().length > 0));
  }

  setDescribedByIds(ids: string[]): void {
    this.describedByIdsState.set(ids.filter((id) => id.trim().length > 0));
  }

  setInvalid(isInvalid: boolean): void {
    this.invalidState.set(isInvalid);
  }

  setRequired(isRequired: boolean): void {
    this.requiredState.set(isRequired);
  }

  setDisabled(isDisabled: boolean | null): void {
    if (isDisabled === null) {
      return;
    }

    this.select.setDisabledState(isDisabled);
    this.changeDetectorRef.markForCheck();
  }

  override focus(): void {
    this.select.focus();
  }

  private readInitialHostId(): string | null {
    const hostId = this.hostElement.nativeElement.getAttribute('id')?.trim();
    return hostId && hostId.length > 0 ? hostId : null;
  }

  private toggleHostClass(className: string, enabled: boolean): void {
    if (enabled) {
      this.renderer.addClass(this.hostElement.nativeElement, className);
      return;
    }

    this.renderer.removeClass(this.hostElement.nativeElement, className);
  }
}
